import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import useColumnConfig, { getStoredPageSize } from '../hooks/useColumnConfig';
import ColumnConfigDrawer from '../components/ColumnConfigDrawer';
import TableFilterDrawer, { TableFilterButton, countActiveFilters } from '../components/TableFilterDrawer';
import { getPartners } from '../modules/partners/api';
import { Alert, Button, Card, Popconfirm, Spin, Table, Tag, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined, ApiOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getWebhookErrors,
  deleteWebhookLog,
  deleteAllWebhookErrors,
  deleteWebhookLogsByIds,
} from '../modules/webhook-logs/api';

const { Paragraph } = Typography;

const fmtTime = (iso) => (iso ? dayjs(iso).format('DD MMM YYYY HH:mm:ss') : '—');

/**
 * Monitoring → Webhook errors. Cross-order list of partner-webhook calls that
 * did NOT return a 2xx (network error / NON-PROD GUARD block / partner 4xx-5xx).
 * Each row can be deleted (only here — it just removes the WebhookLog entry).
 */
const WebhookErrorsPage = () => {
  const { t } = useTranslation(['routes', 'common']);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // Seeded from the Settings drawer's stored value so the first fetch and the
  // table agree; the drawer keeps both in step afterwards.
  const [pageSize, setPageSize] = useState(() => getStoredPageSize('webhook-errors-columns', 50));
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // Server-side filters: this list is paginated on the backend, so filtering
  // the visible page client-side would silently lie about the rest.
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  const load = useCallback(
    async (p = page, ps = pageSize, f = filters) => {
      setLoading(true);
      try {
        const { response } = await getWebhookErrors({ take: ps, skip: (p - 1) * ps, ...f });
        setRows(response?.items || []);
        setTotal(response?.total || 0);
      } catch (e) {
        messageApi.error(e?.message || t('common:error'));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, pageSize, filters],
  );

  useEffect(() => {
    load(1, pageSize);
    getPartners({ take: 300 }).then(({ response }) => {
      setPartnerOptions((response?.partners || []).map((pt) => ({ value: pt.id, label: pt.name })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    try {
      const { response } = await deleteWebhookLog(id);
      if (response?.success) {
        messageApi.success(t('webhookErrors.deleted'));
        setSelectedRowKeys((keys) => keys.filter((k) => k !== id));
        // Reload (adjust page if we just emptied the last one).
        const nextTotal = total - 1;
        const lastPage = Math.max(1, Math.ceil(nextTotal / pageSize));
        const targetPage = Math.min(page, lastPage);
        setPage(targetPage);
        load(targetPage, pageSize);
      } else {
        messageApi.error(t('common:error'));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    }
  };

  const handleDeleteAll = async () => {
    try {
      const { response } = await deleteAllWebhookErrors();
      if (response?.success) {
        messageApi.success(t('webhookErrors.deletedAll', { count: response.count ?? 0 }));
        setPage(1);
        load(1, pageSize);
      } else {
        messageApi.error(t('common:error'));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      const { response } = await deleteWebhookLogsByIds(selectedRowKeys);
      if (response?.success) {
        messageApi.success(t('webhookErrors.deletedAll', { count: response.count ?? selectedRowKeys.length }));
        const nextTotal = total - (response.count ?? selectedRowKeys.length);
        const lastPage = Math.max(1, Math.ceil(nextTotal / pageSize));
        const targetPage = Math.min(page, lastPage);
        setSelectedRowKeys([]);
        setPage(targetPage);
        load(targetPage, pageSize);
      } else {
        messageApi.error(t('common:error'));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    }
  };

  const statusTag = (rec) => {
    if (rec.responseStatus == null) {
      const blocked = (rec.errorMessage || '').startsWith('[NON-PROD GUARD]');
      return (
        <Tag color={blocked ? 'default' : 'red'}>
          {blocked ? t('webhookErrors.blocked') : t('webhookErrors.noResponse')}
        </Tag>
      );
    }
    return <Tag color="red">{rec.responseStatus}</Tag>;
  };

  const columns = [
    {
      title: t('webhookErrors.time'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 175,
      render: fmtTime,
    },
    {
      title: t('webhookErrors.order'),
      key: 'order',
      width: 130,
      render: (_, rec) =>
        rec.order?.orderCode ? <Link to={`/admin/orders/${rec.order.orderCode}`}>{rec.order.orderCode}</Link> : '—',
    },
    {
      title: t('webhookErrors.partner'),
      key: 'partner',
      width: 140,
      render: (_, rec) => rec.partner?.name || '—',
    },
    {
      title: t('webhookErrors.event'),
      dataIndex: 'eventType',
      key: 'eventType',
      width: 140,
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: t('webhookErrors.status'),
      key: 'status',
      width: 110,
      render: (_, rec) => statusTag(rec),
    },
    {
      title: t('webhookErrors.errorMessage'),
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <span style={{ color: '#cf1322' }}>{v.length > 80 ? `${v.slice(0, 80)}…` : v}</span>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, rec) => (
        <Popconfirm
          title={t('webhookErrors.deleteConfirm')}
          okText={t('common:delete')}
          okButtonProps={{ danger: true }}
          cancelText={t('common:cancel')}
          onConfirm={() => handleDelete(rec.id)}
        >
          <Button type="text" danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const expanded = (rec) => (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 320px', minWidth: 280 }}>
        <b>{t('webhookErrors.request')}</b>
        <Paragraph code copyable style={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto', fontSize: 12 }}>
          {typeof rec.requestBody === 'string' ? rec.requestBody : JSON.stringify(rec.requestBody, null, 2)}
        </Paragraph>
      </div>
      <div style={{ flex: '1 1 320px', minWidth: 280 }}>
        <b>{t('webhookErrors.response')}</b>
        <Paragraph code copyable style={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto', fontSize: 12 }}>
          {rec.responseBody || rec.errorMessage || '—'}
        </Paragraph>
      </div>
    </div>
  );

  // Same Settings drawer as every other list: it owns column visibility/order
  // AND the page size, so the bottom "N / page" selector is dropped and there
  // is a single source of truth. Changing the size refetches from page 1,
  // because this list is paginated server-side.
  const FIXED_KEYS = ['actions'];
  const columnConfig = useColumnConfig('webhook-errors-columns', columns, FIXED_KEYS);

  return (
    <div className="WebhookErrorsPage Content" style={{ paddingBottom: 24 }}>
      {contextHolder}
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>
              <ApiOutlined style={{ color: '#cf1322', marginRight: 8 }} />
              {t('webhookErrors.title')}
            </h2>
            <div style={{ color: 'var(--text-muted)', maxWidth: 720, marginTop: 4 }}>{t('webhookErrors.subtitle')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={t('webhookErrors.deleteSelectedConfirm', { count: selectedRowKeys.length })}
                okText={t('common:delete')}
                okButtonProps={{ danger: true }}
                cancelText={t('common:cancel')}
                onConfirm={handleDeleteSelected}
              >
                <Button danger icon={<DeleteOutlined />}>
                  {t('webhookErrors.deleteSelected', { count: selectedRowKeys.length })}
                </Button>
              </Popconfirm>
            )}
            {total > 0 && (
              <Popconfirm
                title={t('webhookErrors.deleteAllConfirm', { count: total })}
                okText={t('common:delete')}
                okButtonProps={{ danger: true }}
                cancelText={t('common:cancel')}
                onConfirm={handleDeleteAll}
              >
                <Button danger icon={<DeleteOutlined />}>
                  {t('webhookErrors.deleteAll')}
                </Button>
              </Popconfirm>
            )}
            <Button type="primary" icon={<ReloadOutlined />} loading={loading} onClick={() => load(page, pageSize)}>
              {t('webhookErrors.refresh')}
            </Button>
            <TableFilterButton activeCount={countActiveFilters(filters)} onClick={() => setFilterOpen(true)} />
            <Button
              type="text"
              title="Settings"
              onClick={columnConfig.openDrawer}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <ReactSVG className="icon" src="/icons/settings.svg" />
            </Button>
          </div>
        </div>

        <Spin spinning={loading}>
          {rows.length === 0 && !loading ? (
            <Alert type="success" showIcon message={t('webhookErrors.allClear')} />
          ) : (
            <Table
              rowKey="id"
              size="small"
              columns={columnConfig.visibleColumns}
              dataSource={rows}
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
              }}
              expandable={{ expandedRowRender: expanded }}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: false,
                onChange: (p, ps) => {
                  setSelectedRowKeys([]);
                  setPage(p);
                  setPageSize(ps);
                  load(p, ps);
                },
              }}
            />
          )}
        </Spin>
        <TableFilterDrawer
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          values={filters}
          onApply={(f) => {
            setFilters(f);
            setSelectedRowKeys([]);
            setPage(1);
            load(1, pageSize, f);
          }}
          fields={[
            { name: 'partnerId', label: t('webhookErrors.partner'), options: partnerOptions, placeholder: '—' },
            {
              name: 'eventType',
              label: t('webhookErrors.event'),
              placeholder: '—',
              options: [
                'order.dispatched',
                'order.updated',
                'order.arriving_soon',
                'route.started',
                'route.ended',
                'step.pickup',
                'step.delivery',
                'step.service',
                'step.unchecked',
              ].map((v) => ({ value: v, label: v })),
            },
          ]}
        />
        <ColumnConfigDrawer
          open={columnConfig.drawerOpen}
          onClose={columnConfig.closeDrawer}
          columns={columnConfig.orderedAllColumns}
          hidden={columnConfig.hidden}
          fixedKeys={FIXED_KEYS}
          onToggle={columnConfig.toggleColumn}
          onMove={columnConfig.moveColumn}
          onReset={columnConfig.resetConfig}
          pageSize={columnConfig.pageSize}
          onPageSizeChange={(size) => {
            columnConfig.setPageSize(size);
            setPageSize(size);
            setPage(1);
            load(1, size);
          }}
        />
      </Card>
    </div>
  );
};

export default WebhookErrorsPage;
